// src/pages/CheckoutPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart }        from '../context/CartContext';
import { useAuth }        from '../context/AuthContext';
import { createOrder }   from '../firebase/services';
import Toast             from '../components/Toast';
import './OrderConfirmed.css';

/* ── Razorpay SDK loader ── */
function loadRazorpay() {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// ⚠️ Replace with your key from dashboard.razorpay.com
const RAZORPAY_KEY   = 'rzp_test_XXXXXXXXXXXXXXXX';
const STEPS          = ['Information', 'Delivery', 'Payment'];
const DELIVERY_FEE   = 40;
const PACKING_CHARGE = 10;

export default function CheckoutPage() {
  const { cart, subtotal, clearCart } = useCart();
  const { user, userData }            = useAuth();
  const navigate                      = useNavigate();

  const [step,        setStep]        = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [toast,       setToast]       = useState(null);
  const [done,        setDone]        = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [isMobile,    setIsMobile]    = useState(window.innerWidth < 720);
  const [showSummary, setShowSummary] = useState(false);

  const [form, setForm] = useState({
    receiverName: '', email: '', phone: '',
    address: '', city: '', state: '', zip: '',
    addressType: 'Home',
    shipping: 'standard',
    payment: 'cod',
  });

  /* Detect mobile resize */
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  /* Auto-fill from user */
  useEffect(() => {
    if (!user) return;
    setForm(f => ({
      ...f,
      receiverName: userData?.displayName || user.displayName || '',
      email:        userData?.email       || user.email       || '',
      phone:        userData?.phone       || '',
    }));
  }, [userData, user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!user)                        { navigate('/login', { state: { from: '/checkout' } }); return null; }
  if (cart.length === 0 && !done)   { navigate('/cart'); return null; }

  /* ── Totals ── */
  const expressExtra   = form.shipping === 'express' ? 25 : 0;
  const deliveryCharge = subtotal >= 500 ? 0 : DELIVERY_FEE + expressExtra;
  const packingCharge  = PACKING_CHARGE;
  const platformFee    = 0;
  const tipAmount      = 0;
  const discount       = 0;
  const tax            = 0;
  const itemSubTotal   = subtotal;
  const totalAmount    = itemSubTotal + deliveryCharge + packingCharge - discount;
  const totalPaise     = Math.round(totalAmount * 100);
  const rewardPoints   = Math.round(totalAmount);

  const buildOrderDetails = () =>
    cart.map(item => ({
      id:                    item.id            || '',
      ItemName:              item.name          || item.ItemName || '',
      ItemPrice:             item.price         || 0,
      originalPrice:         item.price         || 0,
      finalPrice:            item.price         || 0,
      discountPrice:         (item.price || 0).toFixed(2),
      ItemDiscount:          0,
      quantity:              item.qty           || 1,
      ImageBlob:             item.image         || item.ImageBlob || '',
      vendorId:              item.vendorId      || '',
      vendorName:            item.vendorName    || '',
      totalDiscountedPrice:  itemSubTotal,
      totalItemOriginalPrice:itemSubTotal,
      createdAtItem:         new Date(),
    }));

  const buildAddress = () => ({
    receiverName: form.receiverName,
    address:      `${form.address}, ${form.city}, ${form.state}, ${form.zip}`,
    phone:        form.phone,
    type:         form.addressType,
    addressType:  form.addressType,
    extraDetails: '',
    coords: { latitude: 0, longitude: 0 },
  });

  const vendorId   = cart[0]?.vendorId   || '';
  const vendorName = cart[0]?.vendorName || '';

  const orderPayload = (extra = {}) => ({
    customerName:        form.receiverName,
    phoneNumber:         form.phone,
    address:             buildAddress(),
    orderDetails:        buildOrderDetails(),
    itemSubTotal,
    subtotal:            itemSubTotal,
    deliveryCharge,
    vendorPackingCharge: packingCharge,
    platformFee,
    tipAmount,
    discount,
    discountAmountOffer: 0,
    offerPercentage:     0,
    tax,
    taxRate:             0,
    totalAmount,
    rewardPointsEarned:  rewardPoints,
    couponCode:          '',
    couponid:            0,
    deliveryType:        'delivery',
    orderMode:           'delivery',
    orderType:           'instant',
    vendorId,
    vendorName,
    ...extra,
  });

  /* ── COD ── */
  const placeCOD = () => createOrder(user.uid, orderPayload({ paymentMethod: 'COD' }));

  /* ── Razorpay ── */
  const placeOnline = async () => {
    const ok = await loadRazorpay();
    if (!ok) throw new Error('Razorpay failed to load. Check your connection.');
    return new Promise((resolve, reject) => {
      new window.Razorpay({
        key: RAZORPAY_KEY, amount: totalPaise, currency: 'INR',
        name: vendorName || 'ShopWorld',
        description: `Order from ${vendorName || 'ShopWorld'}`,
        handler: async (r) => {
          try {
            const result = await createOrder(user.uid, orderPayload({
              paymentMethod:     'Online',
              OrderId:           r.razorpay_payment_id || '',
              razorpayPaymentId: r.razorpay_payment_id || '',
              razorpayOrderId:   r.razorpay_order_id   || '',
              razorpaySignature: r.razorpay_signature  || '',
            }));
            resolve(result);
          } catch (err) { reject(err); }
        },
        prefill: { name: form.receiverName, email: form.email, contact: `+91${form.phone}` },
        theme: { color: '#3bb77e' },
        modal: { ondismiss: () => { setLoading(false); reject(new Error('cancelled')); } },
      }).open();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 2) { setStep(s => s + 1); return; }
    setLoading(true);
    try {
      const result = form.payment === 'cod' ? await placeCOD() : await placeOnline();
      clearCart();
      setOrderResult(result);
      setDone(true);
    } catch (err) {
      if (err.message !== 'cancelled')
        setToast({ message: err.message || 'Order failed. Try again.', type: 'error' });
      setLoading(false);
    }
  };

  /* ─────────────────────── SUCCESS ─────────────────────── */
  if (done && orderResult) return (
    <div className="oc-page">

      {/* ── Top banner ── */}
      <div className="oc-banner">
        <div className="oc-check-ring">
          {form.payment === 'cod' ? '📦' : '✓'}
        </div>
        <h2 className="oc-banner-title">Order Confirmed!</h2>
        <p className="oc-banner-sub">
          Thank you, {form.receiverName}. Your order has been placed successfully.
        </p>
        <div className="oc-order-id-pill">
          <span className="oc-order-id-pill-label">Order ID</span>
          <span className="oc-order-id-pill-value">{orderResult.orderId}</span>
        </div>
      </div>

      <div className="oc-cards">

        {/* ── Payment status card ── */}
        <div className={`oc-card ${form.payment === 'cod' ? 'oc-payment-cod' : 'oc-payment-card'}`}>
          <div className="oc-card-header">
            <div className="oc-card-header-icon">
              {form.payment === 'cod' ? '💵' : '💳'}
            </div>
            <span className="oc-card-header-title">Payment Details</span>
          </div>
          <div className="oc-card-body">
            <div className="oc-payment-status">
              <div className="oc-payment-method">
                <div className="oc-pay-method-icon">
                  {form.payment === 'cod' ? '💵' : '✅'}
                </div>
                <div>
                  <div className="oc-pay-method-name">
                    {form.payment === 'cod' ? 'Cash on Delivery' : 'Online Payment'}
                  </div>
                  <div className="oc-pay-method-sub">
                    {form.payment === 'cod' ? 'Pay when your order arrives' : 'Payment received successfully'}
                  </div>
                </div>
              </div>
              <div className="oc-payment-amount">
                <div className="oc-amount-label">Total Amount</div>
                <div className="oc-amount-value">₹{totalAmount.toFixed(2)}</div>
              </div>
            </div>
            {form.payment === 'cod' && (
              <div className="oc-cod-note">
                Please keep exact change of <strong>₹{totalAmount.toFixed(2)}</strong> ready when your delivery partner arrives.
              </div>
            )}
          </div>
        </div>

        {/* ── Items ordered card ── */}
        <div className="oc-card">
          <div className="oc-card-header">
            <div className="oc-card-header-icon" style={{background:'#f4f6fa'}}>🛍</div>
            <span className="oc-card-header-title">
              Items Ordered &nbsp;·&nbsp; {cart.length} item{cart.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="oc-card-body">
            {cart.map(item => (
              <div key={item._key} className="oc-item-row">
                <div className="oc-item-img">
                  {item.image
                    ? <img src={item.image} alt={item.name} />
                    : '🍽'}
                </div>
                <div className="oc-item-info">
                  <div className="oc-item-name">{item.name || item.ItemName}</div>
                  <div className="oc-item-qty">Qty: {item.qty}</div>
                </div>
                <span className="oc-item-price">₹{(item.price * item.qty).toFixed(2)}</span>
              </div>
            ))}

            {/* Bill summary */}
            <div className="oc-bill">
              {[
                ['Item Subtotal',   `₹${itemSubTotal.toFixed(2)}`,  false],
                ['Delivery',        deliveryCharge === 0 ? 'Free' : `₹${deliveryCharge}`, deliveryCharge === 0],
                ['Packing Charge',  `₹${packingCharge}`,            false],
              ].map(([k, v, isFree]) => (
                <div key={k} className="oc-bill-row">
                  <span>{k}</span>
                  <span style={{color: isFree ? '#3bb77e' : '#555'}}>{v}</span>
                </div>
              ))}
              <div className="oc-bill-divider" />
              <div className="oc-bill-total">
                <span>Total Paid</span>
                <span className="oc-bill-total-amt">₹{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Delivery address card ── */}
        <div className="oc-card oc-address-card">
          <div className="oc-card-header">
            <div className="oc-card-header-icon" style={{background:'#f0f4ff'}}>📍</div>
            <span className="oc-card-header-title">Delivery Address</span>
          </div>
          <div className="oc-card-body">
            <div className="oc-address-row">
              <div className="oc-address-icon">🏠</div>
              <div>
                <div className="oc-address-name">{form.receiverName}</div>
                <div className="oc-address-line">
                  {form.address}<br />
                  {form.city}, {form.state} — {form.zip}
                </div>
                <div className="oc-address-phone">📞 +91 {form.phone}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Reward points card ── */}
        {rewardPoints > 0 && (
          <div className="oc-card oc-reward-card">
            <div className="oc-card-header">
              <div className="oc-card-header-icon" style={{background:'#fff9f0'}}>🎯</div>
              <span className="oc-card-header-title">Reward Points Earned</span>
            </div>
            <div className="oc-card-body">
              <div className="oc-reward-body">
                <div>
                  <div className="oc-reward-pts">+{rewardPoints}</div>
                  <div className="oc-reward-pts-label">Points Earned</div>
                </div>
                <div className="oc-reward-desc">
                  You've earned <strong>{rewardPoints} reward points</strong> on this order.
                  Keep ordering to unlock exclusive rewards and discounts!
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Action buttons ── */}
      <div className="oc-actions">
        <Link to="/orders" className="oc-btn-primary">📦 Track My Order</Link>
        <Link to="/"       className="oc-btn-secondary">← Back to Home</Link>
      </div>

    </div>
  );

  /* ─────────────────────── MAIN ─────────────────────── */
  const canContinue = !(step === 0 && form.phone.length < 10);

  return (
    <div className="co-page">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ── Top bar ── */}
      <div className="co-topbar">
        <Link to="/" className="co-logo">ShopWorld</Link>

        {/* Step indicator */}
        <div className="co-steps">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`co-step ${i <= step ? 'co-step-done' : ''} ${i === step ? 'co-step-active' : ''}`}>
                <div className="co-step-circle">{i < step ? '✓' : i + 1}</div>
                <span className="co-step-label">{label}</span>
              </div>
              {i < 2 && <div className={`co-step-line ${i < step ? 'co-step-line-done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="co-secure">🔒 Secure</div>
      </div>

      {/* ── Mobile: order summary toggle ── */}
      {isMobile && (
        <div className="co-mobile-summary-toggle" onClick={() => setShowSummary(v => !v)}>
          <span>🛒 Order Summary — ₹{totalAmount.toFixed(2)}</span>
          <span style={{ transition: 'transform 0.25s', transform: showSummary ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
        </div>
      )}

      {/* ── Mobile: collapsible summary ── */}
      {isMobile && showSummary && <OrderSummary cart={cart} itemSubTotal={itemSubTotal} deliveryCharge={deliveryCharge} packingCharge={packingCharge} platformFee={platformFee} discount={discount} totalAmount={totalAmount} rewardPoints={rewardPoints} form={form} mobile />}

      {/* ── Main grid ── */}
      <div className="co-grid">

        {/* ── FORM ── */}
        <form onSubmit={handleSubmit} className="co-form">

          {/* STEP 0 — Contact */}
          {step === 0 && (
            <div className="co-section">
              <h2 className="co-section-title">Contact Information</h2>
              <p className="co-section-sub">Auto-filled from your account — edit if needed.</p>

              <div className="co-field">
                <label className="co-label">Full Name *</label>
                <input className="co-input" value={form.receiverName}
                  onChange={e => set('receiverName', e.target.value)}
                  required placeholder="Your full name" />
              </div>

              <div className="co-field">
                <label className="co-label">Email Address</label>
                <input className="co-input co-input-readonly" type="email" value={form.email} readOnly />
                <p className="co-hint">Linked to your account — cannot be changed here.</p>
              </div>

              <div className="co-field">
                <label className="co-label">
                  Phone Number * <span className="co-label-red">required for delivery</span>
                </label>
                <div className="co-phone-wrap">
                  <span className="co-phone-prefix">+91</span>
                  <input className="co-input co-input-phone" type="tel"
                    value={form.phone}
                    onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    required minLength={10} maxLength={10}
                    placeholder="98765 43210" />
                </div>
                {form.phone.length > 0 && form.phone.length < 10 && (
                  <p className="co-hint co-hint-red">⚠️ Enter a valid 10-digit number</p>
                )}
                {form.phone.length === 10 && (
                  <p className="co-hint co-hint-green">✓ Valid phone number</p>
                )}
              </div>
            </div>
          )}

          {/* STEP 1 — Delivery */}
          {step === 1 && (
            <div className="co-section">
              <h2 className="co-section-title">Delivery Address</h2>

              <div className="co-field">
                <label className="co-label">Street / House Address *</label>
                <input className="co-input" value={form.address}
                  onChange={e => set('address', e.target.value)} required
                  placeholder="House no, Street, Locality" />
              </div>

              <div className="co-grid2">
                <div className="co-field">
                  <label className="co-label">City *</label>
                  <input className="co-input" value={form.city}
                    onChange={e => set('city', e.target.value)} required placeholder="Chennai" />
                </div>
                <div className="co-field">
                  <label className="co-label">State *</label>
                  <input className="co-input" value={form.state}
                    onChange={e => set('state', e.target.value)} required placeholder="Tamil Nadu" />
                </div>
              </div>

              <div className="co-grid2">
                <div className="co-field">
                  <label className="co-label">Postal Code *</label>
                  <input className="co-input" value={form.zip}
                    onChange={e => set('zip', e.target.value.replace(/\D/g,'').slice(0,6))}
                    required maxLength={6} placeholder="629171" />
                </div>
                <div className="co-field">
                  <label className="co-label">Address Type</label>
                  <div className="co-addr-types">
                    {['Home','Work','Other'].map(t => (
                      <button key={t} type="button"
                        onClick={() => set('addressType', t)}
                        className={`co-addr-btn ${form.addressType === t ? 'co-addr-btn-active' : ''}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Delivery method */}
              <h3 className="co-subsection-title">Delivery Method</h3>
              {[
                { id:'standard', label:'Standard Delivery', sub:'3–5 business days', fee: subtotal>=500?0:DELIVERY_FEE },
                { id:'express',  label:'Express Delivery',  sub:'Next business day',  fee: subtotal>=500?25:DELIVERY_FEE+25 },
              ].map(opt => (
                <div key={opt.id}
                  onClick={() => set('shipping', opt.id)}
                  className={`co-radio-card ${form.shipping === opt.id ? 'co-radio-card-active' : ''}`}>
                  <div className="co-radio-left">
                    <div className={`co-radio-dot ${form.shipping === opt.id ? 'co-radio-dot-active' : ''}`}>
                      {form.shipping === opt.id && <div className="co-radio-inner" />}
                    </div>
                    <div>
                      <div className="co-radio-label">{opt.label}</div>
                      <div className="co-radio-sub">{opt.sub}</div>
                    </div>
                  </div>
                  <span className={`co-radio-price ${opt.fee===0?'co-price-free':''}`}>
                    {opt.fee === 0 ? 'Free' : `₹${opt.fee}`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* STEP 2 — Payment */}
          {step === 2 && (
            <div className="co-section">
              <h2 className="co-section-title">Payment Method</h2>

              <div className="co-pay-grid">
                {[
                  { id:'cod',    icon:'💵', label:'Cash on Delivery', sub:'Pay when delivered' },
                  { id:'online', icon:'💳', label:'Online Payment',   sub:'UPI · Card · Net Banking' },
                ].map(opt => (
                  <div key={opt.id}
                    onClick={() => set('payment', opt.id)}
                    className={`co-pay-card ${form.payment === opt.id ? 'co-pay-card-active' : ''}`}>
                    <div className="co-pay-icon">{opt.icon}</div>
                    <div className="co-pay-label">{opt.label}</div>
                    <div className="co-pay-sub">{opt.sub}</div>
                  </div>
                ))}
              </div>

              {form.payment === 'cod' && (
                <div className="co-info-box co-info-yellow">
                  📦 Pay <strong>₹{totalAmount.toFixed(2)}</strong> in cash when your order arrives. Keep exact change ready.
                </div>
              )}

              {form.payment === 'online' && (
                <div className="co-info-box co-info-green">
                  <div className="co-rzp-header">
                    <img src="https://razorpay.com/favicon.ico" width={16} height={16} alt="rp" style={{borderRadius:3}} />
                    <span className="co-rzp-title">Powered by Razorpay</span>
                  </div>
                  <p className="co-rzp-desc">Secure payment — UPI, Debit/Credit Cards, Net Banking &amp; Wallets.</p>
                  <div className="co-rzp-badges">
                    {['UPI','GPay','PhonePe','Visa','Mastercard','RuPay','Net Banking'].map(m => (
                      <span key={m} className="co-rzp-badge">{m}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="co-secure-note">
                <span>🔒</span>
                <span>Your data is fully encrypted and secure.</span>
              </div>
            </div>
          )}

          {/* ── Nav buttons ── */}
          <div className="co-nav-btns">
            {step > 0 && (
              <button type="button" onClick={() => setStep(s => s - 1)} className="co-btn-white co-btn-back">← Back</button>
            )}
            <button type="submit" disabled={loading || !canContinue}
              className={`co-btn-green co-btn-submit ${(!canContinue || loading) ? 'co-btn-disabled' : ''}`}>
              {loading ? '⏳ Processing…'
                : step < 2 ? 'Continue →'
                : form.payment === 'cod' ? '📦 Place Order (COD)'
                : `💳 Pay ₹${totalAmount.toFixed(2)}`}
            </button>
          </div>
        </form>

        {/* ── Desktop order summary ── */}
        {!isMobile && (
          <OrderSummary cart={cart} itemSubTotal={itemSubTotal} deliveryCharge={deliveryCharge}
            packingCharge={packingCharge} platformFee={platformFee} discount={discount}
            totalAmount={totalAmount} rewardPoints={rewardPoints} form={form} />
        )}
      </div>

      <CheckoutStyles />
    </div>
  );
}

/* ─────────────────────── ORDER SUMMARY ─────────────────────── */
function OrderSummary({ cart, itemSubTotal, deliveryCharge, packingCharge, platformFee, discount, totalAmount, rewardPoints, form, mobile }) {
  return (
    <div className={`co-summary ${mobile ? 'co-summary-mobile' : ''}`}>
      <h3 className="co-summary-title">Order Summary</h3>

      <div className="co-summary-items">
        {cart.map(item => (
          <div key={item._key} className="co-summary-item">
            <div className="co-summary-img">
              {item.image
                ? <img src={item.image} alt={item.name} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                : <span>🛍</span>}
            </div>
            <div className="co-summary-item-info">
              <div className="co-summary-item-name">{item.name || item.ItemName}</div>
              <div className="co-summary-item-qty">Qty: {item.qty}</div>
            </div>
            <span className="co-summary-item-price">₹{(item.price * item.qty).toFixed(2)}</span>
          </div>
        ))}
      </div>

      <div className="co-summary-rows">
        {[
          ['Item Subtotal',   `₹${itemSubTotal.toFixed(2)}`,        false],
          ['Delivery',        deliveryCharge===0?'Free':`₹${deliveryCharge}`, deliveryCharge===0],
          ['Packing Charge',  `₹${packingCharge}`,                  false],
          ['Platform Fee',    platformFee===0?'Free':`₹${platformFee}`,       platformFee===0],
          ['Discount',        discount>0?`-₹${discount}`:'—',       false],
        ].map(([k, v, isFree]) => (
          <div key={k} className="co-summary-row">
            <span>{k}</span>
            <span style={{color: isFree||v==='—'?'#ccc': v.startsWith('-')?'#3bb77e':'#555'}}>{v}</span>
          </div>
        ))}
      </div>

      <div className="co-summary-divider" />

      <div className="co-summary-total">
        <span>Total</span>
        <span className="co-summary-total-amount">₹{totalAmount.toFixed(2)}</span>
      </div>

      <div className="co-summary-reward">🎯 Earn <strong>{rewardPoints}</strong> reward points</div>
      <div className="co-summary-pay-method">
        {form.payment==='cod' ? '💵 Cash on Delivery' : '💳 Online — Razorpay'}
      </div>
    </div>
  );
}

/* ─────────────────────── ALL STYLES ─────────────────────── */
function CheckoutStyles() {
  return (
    <style>{`
      /* ── Page ── */
      .co-page {
        padding-top: 68px;
        background: #f4f6fa;
        min-height: 100vh;
        font-family: 'Poppins', sans-serif;
      }

      /* ── Top bar ── */
      .co-topbar {
        background: #fff;
        border-bottom: 1px solid #ececec;
        padding: 14px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 10px;
        position: sticky;
        top: 68px;
        z-index: 100;
      }
      .co-logo {
        font-family: 'Playfair Display', serif;
        font-size: 20px;
        color: #253d4e;
        text-decoration: none;
        font-weight: 700;
        flex-shrink: 0;
      }
      .co-secure {
        font-size: 12px;
        color: #3bb77e;
        font-weight: 600;
        flex-shrink: 0;
      }

      /* ── Steps ── */
      .co-steps {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;
        justify-content: center;
      }
      .co-step {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .co-step-circle {
        width: 26px; height: 26px;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: 700;
        background: #eee; color: #aaa;
        transition: all 0.3s;
      }
      .co-step-active .co-step-circle  { background: #253d4e; color: #fff; }
      .co-step-done   .co-step-circle  { background: #3bb77e; color: #fff; }
      .co-step-label {
        font-size: 12px; font-weight: 500; color: #aaa;
      }
      .co-step-active .co-step-label   { color: #253d4e; }
      .co-step-done   .co-step-label   { color: #3bb77e; }
      .co-step-line {
        width: 20px; height: 2px;
        background: #e8e8e8;
        transition: background 0.3s;
      }
      .co-step-line-done { background: #3bb77e; }

      /* ── Mobile summary toggle ── */
      .co-mobile-summary-toggle {
        background: #253d4e;
        color: #fff;
        padding: 13px 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }

      /* ── Main grid ── */
      .co-grid {
        max-width: 980px;
        margin: 0 auto;
        padding: 24px 18px;
        display: grid;
        grid-template-columns: 1fr 310px;
        gap: 22px;
        align-items: start;
      }

      /* ── Form card ── */
      .co-form {
        background: #fff;
        border-radius: 14px;
        border: 1px solid #ececec;
        padding: 26px 24px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.04);
      }

      /* ── Section ── */
      .co-section-title {
        font-family: 'Playfair Display', serif;
        font-size: 20px;
        color: #253d4e;
        margin: 0 0 4px;
      }
      .co-section-sub {
        font-size: 12px; color: #aaa; margin: 0 0 20px;
      }
      .co-subsection-title {
        font-family: 'Playfair Display', serif;
        font-size: 16px; color: #253d4e;
        margin: 18px 0 12px;
      }

      /* ── Fields ── */
      .co-field { margin-bottom: 14px; }
      .co-label {
        display: block;
        font-size: 11px; font-weight: 600;
        letter-spacing: 0.07em; text-transform: uppercase;
        color: #253d4e; margin-bottom: 6px;
      }
      .co-label-red { color: #f94a4a; text-transform: none; letter-spacing: 0; }
      .co-input {
        width: 100%;
        padding: 11px 14px;
        border: 1.5px solid #e8e8e8;
        border-radius: 8px;
        font-size: 14px; color: #253d4e;
        outline: none;
        background: #fff;
        font-family: 'Poppins', sans-serif;
        transition: border-color 0.2s;
        box-sizing: border-box;
      }
      .co-input:focus { border-color: #3bb77e; }
      .co-input-readonly { background: #f9f9f9; color: #999; cursor: not-allowed; }
      .co-hint { font-size: 11px; color: #aaa; margin: 4px 0 0; }
      .co-hint-red   { color: #f94a4a; }
      .co-hint-green { color: #3bb77e; }

      /* ── Phone ── */
      .co-phone-wrap { position: relative; }
      .co-phone-prefix {
        position: absolute; left: 14px; top: 50%;
        transform: translateY(-50%);
        font-size: 13px; color: #666; font-weight: 600;
        pointer-events: none;
      }
      .co-input-phone { padding-left: 46px; }

      /* ── 2-col grid ── */
      .co-grid2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
      }

      /* ── Address type buttons ── */
      .co-addr-types { display: flex; gap: 8px; margin-top: 2px; }
      .co-addr-btn {
        flex: 1; padding: 10px 4px;
        font-size: 12px; font-weight: 600;
        border-radius: 8px;
        border: 1.5px solid #e8e8e8;
        background: #fafafa; color: #888;
        cursor: pointer; transition: all 0.2s;
      }
      .co-addr-btn-active {
        border-color: #3bb77e;
        background: #f0fdf8;
        color: #065f46;
      }

      /* ── Radio card (delivery method) ── */
      .co-radio-card {
        border: 1.5px solid #e8e8e8;
        background: #fff;
        border-radius: 10px;
        padding: 13px 16px;
        margin-bottom: 10px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        transition: all 0.2s;
      }
      .co-radio-card-active {
        border-color: #3bb77e;
        background: #f0fdf8;
      }
      .co-radio-left { display: flex; align-items: center; gap: 10px; }
      .co-radio-dot {
        width: 16px; height: 16px; border-radius: 50%;
        border: 2px solid #ccc; background: #fff;
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0; transition: all 0.2s;
      }
      .co-radio-dot-active { border-color: #3bb77e; background: #3bb77e; }
      .co-radio-inner { width: 5px; height: 5px; border-radius: 50%; background: #fff; }
      .co-radio-label { font-size: 13px; font-weight: 600; color: #253d4e; }
      .co-radio-sub   { font-size: 11px; color: #999; margin-top: 1px; }
      .co-radio-price { font-weight: 700; font-size: 14px; color: #253d4e; }
      .co-price-free  { color: #3bb77e; }

      /* ── Payment cards ── */
      .co-pay-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        margin-bottom: 18px;
      }
      .co-pay-card {
        border: 2px solid #e8e8e8;
        background: #fafafa;
        border-radius: 10px;
        padding: 16px 10px;
        cursor: pointer;
        text-align: center;
        transition: all 0.2s;
      }
      .co-pay-card-active { border-color: #3bb77e; background: #f0fdf8; }
      .co-pay-icon  { font-size: 26px; margin-bottom: 6px; }
      .co-pay-label { font-size: 13px; font-weight: 700; color: #253d4e; }
      .co-pay-sub   { font-size: 10px; color: #aaa; margin-top: 3px; }

      /* ── Info boxes ── */
      .co-info-box {
        border-radius: 8px;
        padding: 12px 15px;
        margin-bottom: 14px;
        font-size: 13px;
        font-weight: 500;
        line-height: 1.5;
      }
      .co-info-yellow { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
      .co-info-green  { background: #f0fdf8; border: 1px solid #a7f3d0; color: #065f46; }
      .co-rzp-header  { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
      .co-rzp-title   { font-size: 13px; font-weight: 700; }
      .co-rzp-desc    { font-size: 12px; margin: 0 0 8px; }
      .co-rzp-badges  { display: flex; gap: 6px; flex-wrap: wrap; }
      .co-rzp-badge {
        font-size: 10px; padding: 2px 7px;
        background: #fff; border: 1px solid #a7f3d0;
        border-radius: 4px; color: #065f46; font-weight: 600;
      }
      .co-secure-note {
        display: flex; align-items: center; gap: 7px;
        padding: 9px 13px; background: #f9f9f9;
        border-radius: 7px; border: 1px solid #ececec;
        font-size: 11px; color: #aaa;
      }

      /* ── Nav buttons ── */
      .co-nav-btns {
        display: flex; gap: 10px; margin-top: 24px;
      }
      .co-btn-green {
        display: inline-flex; align-items: center; justify-content: center;
        padding: 13px 24px;
        background: #3bb77e; color: #fff;
        border-radius: 8px; font-weight: 600; font-size: 14px;
        text-decoration: none; border: none; cursor: pointer;
        transition: background 0.2s;
        font-family: 'Poppins', sans-serif;
      }
      .co-btn-green:hover { background: #2da06c; }
      .co-btn-white {
        display: inline-flex; align-items: center; justify-content: center;
        padding: 13px 24px;
        background: #fff; color: #253d4e;
        border-radius: 8px; font-weight: 500; font-size: 14px;
        text-decoration: none; border: 1.5px solid #e8e8e8; cursor: pointer;
        font-family: 'Poppins', sans-serif;
      }
      .co-btn-back   { padding: 12px 18px; font-size: 13px; }
      .co-btn-submit { flex: 1; }
      .co-btn-disabled { opacity: 0.55; cursor: not-allowed !important; }

      /* ── Order summary (desktop) ── */
      .co-summary {
        background: #fff;
        border-radius: 14px;
        border: 1px solid #ececec;
        padding: 20px;
        position: sticky;
        top: 120px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.04);
      }
      .co-summary-mobile {
        position: static;
        border-radius: 0;
        border: none;
        border-bottom: 1px solid #ececec;
      }
      .co-summary-title {
        font-family: 'Playfair Display', serif;
        font-size: 16px; color: #253d4e; margin: 0 0 16px;
      }
      .co-summary-items {
        max-height: 200px;
        overflow-y: auto;
        margin-bottom: 14px;
      }
      .co-summary-item {
        display: flex; gap: 10px;
        margin-bottom: 12px; padding-bottom: 12px;
        border-bottom: 1px solid #f4f4f4;
        align-items: center;
      }
      .co-summary-img {
        width: 46px; height: 54px;
        border-radius: 6px; overflow: hidden;
        border: 1px solid #ececec; flex-shrink: 0;
        background: #f9f9f9;
        display: flex; align-items: center; justify-content: center;
        font-size: 18px;
      }
      .co-summary-item-info { flex: 1; min-width: 0; }
      .co-summary-item-name {
        font-size: 12px; font-weight: 600; color: #253d4e;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .co-summary-item-qty  { font-size: 11px; color: #aaa; margin-top: 2px; }
      .co-summary-item-price{ font-size: 12px; font-weight: 700; color: #3bb77e; flex-shrink: 0; }
      .co-summary-rows { margin-bottom: 6px; }
      .co-summary-row {
        display: flex; justify-content: space-between;
        font-size: 12px; color: #888; margin-bottom: 7px;
      }
      .co-summary-divider { height: 1px; background: #ececec; margin: 10px 0; }
      .co-summary-total {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 12px;
        font-size: 14px; font-weight: 700; color: #253d4e;
      }
      .co-summary-total-amount {
        font-family: 'Playfair Display', serif;
        font-size: 20px; color: #3bb77e;
      }
      .co-summary-reward {
        background: #f0fdf8; border-radius: 7px;
        padding: 8px 12px; font-size: 11px;
        color: #065f46; font-weight: 600;
        margin-bottom: 8px; text-align: center;
      }
      .co-summary-pay-method {
        background: #f9f9f9; border: 1px solid #ececec;
        border-radius: 7px; padding: 8px 12px;
        font-size: 11px; color: #888; text-align: center;
      }

      /* ── Success screen ── */
      .co-success-wrap {
        padding-top: 80px; min-height: 90vh;
        display: flex; align-items: center; justify-content: center;
        background: #f4f6fa; padding: 80px 18px 40px;
      }
      .co-success-card {
        text-align: center; max-width: 460px; width: 100%;
        padding: 40px 28px; background: #fff;
        border-radius: 16px; border: 1px solid #ececec;
        box-shadow: 0 8px 40px rgba(0,0,0,0.06);
        animation: co-pop 0.4s ease;
      }
      .co-success-icon  { font-size: 60px; margin-bottom: 14px; }
      .co-success-title {
        font-family: 'Playfair Display', serif;
        font-size: 26px; color: #253d4e; margin: 0 0 8px;
      }
      .co-success-sub  { color: #666; font-size: 14px; margin: 0 0 16px; }
      .co-order-id-box {
        margin: 0 0 14px;
        background: #f0fdf8; border: 1px solid #a7f3d0;
        border-radius: 10px; padding: 12px 16px;
      }
      .co-order-id-label { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 3px; }
      .co-order-id-value { font-family: monospace; font-size: 14px; font-weight: 700; color: #065f46; }
      .co-reward-note { font-size: 12px; color: #aaa; margin: 0 0 20px; }
      .co-success-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }

      @keyframes co-pop {
        from { opacity:0; transform:scale(0.9); }
        to   { opacity:1; transform:scale(1);   }
      }

      /* ════════════════════════════
         RESPONSIVE — TABLET ≤ 768px
         ════════════════════════════ */
      @media (max-width: 768px) {
        .co-topbar { padding: 12px 16px; top: 60px; }
        .co-logo   { font-size: 17px; }
        .co-step-label { display: none; }   /* hide labels, show circles only */
        .co-step-line  { width: 14px; }

        .co-grid {
          grid-template-columns: 1fr;
          padding: 16px 14px;
          gap: 16px;
        }

        .co-form { padding: 20px 16px; }

        .co-grid2 { grid-template-columns: 1fr 1fr; gap: 10px; }

        .co-pay-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
        .co-pay-card { padding: 13px 8px; }
        .co-pay-icon  { font-size: 22px; }
        .co-pay-label { font-size: 11px; }

        .co-btn-green, .co-btn-white { font-size: 13px; padding: 12px 16px; }
      }

      /* ════════════════════════════
         RESPONSIVE — MOBILE ≤ 480px
         ════════════════════════════ */
      @media (max-width: 480px) {
        .co-page { padding-top: 60px; }
        .co-topbar { padding: 10px 14px; flex-wrap: nowrap; }
        .co-secure { font-size: 11px; }

        .co-grid { padding: 12px 10px; gap: 12px; }
        .co-form  { padding: 18px 14px; border-radius: 10px; }

        .co-section-title { font-size: 18px; }

        .co-grid2 {
          grid-template-columns: 1fr;   /* stack on very small screens */
          gap: 0;
        }

        .co-addr-types { gap: 6px; }
        .co-addr-btn   { padding: 9px 2px; font-size: 11px; }

        .co-pay-grid   { grid-template-columns: 1fr 1fr; gap: 8px; }
        .co-pay-card   { padding: 12px 6px; }
        .co-pay-icon   { font-size: 20px; margin-bottom: 4px; }
        .co-pay-label  { font-size: 11px; }
        .co-pay-sub    { font-size: 9px; }

        .co-radio-card  { padding: 11px 12px; }
        .co-radio-label { font-size: 12px; }
        .co-radio-sub   { font-size: 10px; }

        .co-nav-btns { flex-direction: column; gap: 8px; }
        .co-btn-back { width: 100%; justify-content: center; }

        .co-input { font-size: 13px; padding: 10px 12px; }
        .co-label { font-size: 10px; }

        .co-success-card  { padding: 28px 18px; }
        .co-success-title { font-size: 22px; }
        .co-success-icon  { font-size: 50px; }
      }

      /* ════════════════════════════
         RESPONSIVE — SMALL ≤ 360px
         ════════════════════════════ */
      @media (max-width: 360px) {
        .co-steps { gap: 2px; }
        .co-step-circle { width: 22px; height: 22px; font-size: 10px; }
        .co-step-line   { width: 10px; }
        .co-form        { padding: 14px 10px; }
        .co-grid        { padding: 10px 8px; }
      }
    `}</style>
  );
}
